import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { StarRating } from "./StarRating";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sanitizeText, sanitizeName } from "@/lib/sanitize";

interface WriteCompanyReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  onSuccess?: () => void;
}

export const WriteCompanyReviewModal = ({
  isOpen,
  onClose,
  companyId,
  companyName,
  onSuccess,
}: WriteCompanyReviewModalProps) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [serviceDate, setServiceDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error(t("reviews.ratingRequired"));
      return;
    }

    if (comment.length < 50) {
      toast.error(t("reviews.commentTooShort"));
      return;
    }

    setSubmitting(true);
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        toast.error(t("reviews.loginRequired"));
        return;
      }

      const { data: review, error } = await supabase.from("reviews").insert({
        company_id: companyId,
        profile_id: null,
        user_id: user.data.user.id,
        rating,
        title: sanitizeName(title),
        comment: sanitizeText(comment),
        pros: pros ? sanitizeText(pros) : null,
        cons: cons ? sanitizeText(cons) : null,
        service_date: serviceDate || null,
      }).select('id').single();

      if (error) throw error;

      // Notifica all'azienda della nuova recensione
      if (review?.id) {
        try {
          await supabase.functions.invoke('notify-new-review', {
            body: { reviewId: review.id }
          });
        } catch (notifyError) {
          console.error('Error sending review notification:', notifyError);
          // Non bloccare se la notifica fallisce
        }
      }

      toast.success(t("reviews.submitSuccess"));
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error("Error submitting review:", error);
      if (error.code === "23505") {
        toast.error(t("reviews.alreadyReviewed"));
      } else {
        toast.error(t("reviews.submitError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setTitle("");
    setComment("");
    setPros("");
    setCons("");
    setServiceDate("");
    onClose();
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {t("reviews.writeReviewForCompany", { name: companyName })}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Rating */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.yourRating")} *
            </label>
            <StarRating
              rating={rating}
              onChange={setRating}
              size="lg"
              interactive
            />
          </div>

          {/* Service Date */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.serviceDate")}
            </label>
            <BrutalInput
              type="date"
              max={today}
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
            />
          </div>

          {/* Title */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.reviewTitle")} *
            </label>
            <BrutalInput
              type="text"
              maxLength={200}
              required
              placeholder={t("reviews.titlePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Comment */}
          <div>
            <label className="font-bold mb-2 block">
              {t("reviews.yourExperience")} * ({t("reviews.minimum50Chars")})
            </label>
            <textarea
              className="w-full min-h-32 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              required
              minLength={50}
              placeholder={t("reviews.commentPlaceholder")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="text-sm text-muted-foreground mt-1">
              {comment.length}/50 {t("reviews.charactersMinimum")}
            </div>
          </div>

          {/* Pros and Cons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-bold mb-2 block">{t("reviews.pros")}</label>
              <textarea
                className="w-full h-20 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("reviews.prosPlaceholder")}
                value={pros}
                onChange={(e) => setPros(e.target.value)}
              />
            </div>
            <div>
              <label className="font-bold mb-2 block">{t("reviews.cons")}</label>
              <textarea
                className="w-full h-20 px-4 py-3 border-[3px] border-dark bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder={t("reviews.consPlaceholder")}
                value={cons}
                onChange={(e) => setCons(e.target.value)}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <BrutalButton type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" disabled={submitting}>
              {submitting ? t("common.submitting") : t("reviews.publishReview")}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
